import { ArrowTopRightOnSquareIcon } from '@heroicons/react/24/solid'
import mixpanel from 'mixpanel-browser'
import { ContentView, EnrichedContent } from '../../../api/client'
import { isAd } from '../../../util/ads'
import { getPlatformDisplayName } from '../../../util/types'

type Props = {
    content: ContentView
    enrichedContent: EnrichedContent | undefined
    enriching: boolean
}

type GetSocialMediaContentLink = {
    (enrichedContent: EnrichedContent | undefined, content: ContentView): string | undefined
}

/** THIS FOLLOWING TWO FUNCTIONS ARE TECHNICAL DEBT
 * This function is used to generate a link to the original content and post.
 * We've now moved this to `content_link.rs:get_social_media_content_link` => in Rust code
 * HOWEVER - content arrives from content search missing some of the fields that are used to generate the link.
 * In `ContentList.tsx` we do a fetch to get this data from the server which then goes out to the social media services
 * to fetch the required bits. This happens in Tenmoku - we need to migrate this to the Rust code and use the rust function
 * above to generate the.
 *
 * Until then we need to reproduce the logic to generate the url here.
 */

export const getSocialMediaPostLink: GetSocialMediaContentLink = function (
    enrichedContent,
    content
) {
    let postLink: string | undefined
    const isTikTokAd = isAd(content)

    if (enrichedContent && content.account) {
        switch (content.socialMediaServiceName.toLowerCase()) {
            case 'twitter':
                postLink = content.socialMediaPostLink || undefined
                break
            case 'instagram':
                if (!!!enrichedContent.originalPostLinkId) {
                    // We don't have sufficient information to form a link to the original post.
                    break
                } else {
                    postLink = `https://www.instagram.com/p/${enrichedContent.originalPostLinkId}`
                }
                break
            case 'facebook':
                postLink = content.socialMediaPostLink || undefined
                break
            case 'youtube':
                postLink = content.socialMediaPostLink || undefined
                break
            case 'tiktokuser':
                if (!isTikTokAd) {
                    // TikTok Ads do not support direct links to posts.
                    postLink = content.socialMediaPostLink || undefined
                }
                break
            case 'tiktokad':
                if (!isTikTokAd) {
                    // TikTok Ads do not support direct links to posts.
                    postLink = content.socialMediaPostLink || undefined
                }
                break
            default:
                console.error(
                    `Unknown social media service when getting a content link! It was ${content.socialMediaServiceName.toLowerCase}`
                )
        }
    }
    return postLink
}

export const getSocialMediaContentLink: GetSocialMediaContentLink = function (
    enrichedContent,
    content
) {
    let link: string | undefined

    if (enrichedContent && content.account) {
        switch (content.socialMediaServiceName.toLowerCase()) {
            case 'twitter':
                /*
               usernameForUrl should ideally be the screen name of the comment author (sometimes known as handle/username)
               However, as long as the usernameForUrl is a non-empty string of valid characters (alphanumeric/hyphen) then twitter will successfully redirect to the correct comment.
               The below logic removes non-valid characters from the string and if the resulting string is empty it replaces it with 'username' which is a valid string.
               This is to make use of the redirect where mapping has failed.
               */
                let usernameForURL: string
                if (
                    enrichedContent.serviceAuthorName &&
                    enrichedContent.serviceAuthorName.replace(/\W/g, '').length > 0
                ) {
                    usernameForURL = enrichedContent.serviceAuthorName.replace(/\W/g, '')
                } else {
                    usernameForURL = 'username'
                }
                link = `https://twitter.com/${usernameForURL}/status/${content.serviceContentId}`
                break
            case 'instagram':
                if (!!!enrichedContent.originalPostLinkId || !!!content.serviceContentId) {
                    // We don't have sufficient information to form a link to the original post.
                    break
                } else {
                    link = `https://www.instagram.com/p/${enrichedContent.originalPostLinkId}/c/${content.serviceContentId}`
                }
                break
            case 'facebook':
                link = content.originalPostLink || '#'
                break
            case 'youtube':
                link = content.originalPostLink || '#'
                break
            case 'tiktokuser':
                link = content.originalPostLink || '#'
                break
            case 'tiktokad':
                link = content.originalPostLink || '#'
                break
            default:
                console.error(
                    `Unknown social media service when getting a content link! It was ${content.socialMediaServiceName.toLowerCase}`
                )
        }
    }
    return link
}

export default function ContentLink(props: Props) {
    const { content, enrichedContent, enriching } = props

    const isTikTokAd = isAd(content)

    const getSocialMediaContentName = function () {
        if (content.socialMediaServiceName.toLowerCase() === 'twitter') {
            return 'comments'
        } else if (content.socialMediaServiceName.toLowerCase() === 'instagram') {
            return 'comments'
        }
        return 'content'
    }

    let showInTooltipText = `Hidden ${getSocialMediaContentName()} can still be accessed through this link`

    const socialMediaContentLink =
        content.socialMediaContentLink || getSocialMediaContentLink(enrichedContent, content)

    const socialMediaPostLink =
        content.socialMediaPostLink || getSocialMediaPostLink(enrichedContent, content)

    // The Post unavailable tooltip is defined defined as follows:
    const buildPostUnavailableTooltipHtml = (networkLabel?: string) =>
        `
        <div>
            <div>Unable to generate Post link. This can happen for the following reasons:</div>
            
            <ul style="margin: 0.5rem 0 0; padding-left: 1.25rem;">
                <li>- The Post is inaccessible - it has been deleted or auto-moderated on the ${networkLabel ? `${networkLabel}` : ''} platform.</li>
                <li>- Authorisation to access the content has been revoked - have you changed your password or revoked app access?</li>
            </ul>
        </div>
        `

    const postLinkUnavailable = (() => {
        const serviceName = content.socialMediaServiceName?.toLowerCase()
        switch (serviceName) {
            case 'tiktokuser':
                if (isTikTokAd) {
                    return 'TikTok does not support direct links to ads.'
                }
                return buildPostUnavailableTooltipHtml('TikTok')
            case 'tiktokad':
                return 'TikTok Ads do not support direct links to posts.'
            default:
                return buildPostUnavailableTooltipHtml(
                    getPlatformDisplayName(content.socialMediaServiceName)
                )
        }
    })()

    // The Comment unavailable tooltip is defined defined as follows:
    const buildLinkUnavailableTooltipHtml = (networkLabel?: string) =>
        `
        <div>
            <div>Unable to generate Comment link. This can happen for the following reasons:</div>
            
            <ul style="margin: 0.5rem 0 0; padding-left: 1.25rem;">
                <li>- The Post is inaccessible - it has been deleted or auto-moderated on the ${networkLabel ? `${networkLabel}` : ''} platform.</li>
                <li>- Authorisation to access the content has been revoked - have you changed your password or revoked app access?</li>
            </ul>
        </div>
    `.trim()

    const linkUnavailableTooltipHtml = (() => {
        const serviceName = content.socialMediaServiceName?.toLowerCase()
        switch (serviceName) {
            case 'youtube':
                return 'YouTube does not support direct links to comments.'
            case 'tiktokuser':
            case 'tiktokad':
                return 'TikTok does not support direct links to comments.'
            default:
                return buildLinkUnavailableTooltipHtml(
                    getPlatformDisplayName(content.socialMediaServiceName)
                )
        }
    })()

    // Medium cards keep the links as icons; narrow cards drop them and rely on
    // the "Open post" / "Open comment" items in the more-actions menu.
    return (
        <div className="hidden shrink-0 @sm:block">
            <div>
                {enriching ? (
                    <div className="flex h-full w-full animate-pulse flex-row content-center items-stretch justify-center space-x-5 border-solid border-ink-200 pr-5">
                        <div className="h-4 w-24 rounded-sm bg-gray-200"></div>
                    </div>
                ) : (
                    <div className="flex flex-row">
                        <div className="text-arwen-700 hover:text-arwen-800 px-2 ">
                            {socialMediaPostLink ? (
                                <a
                                    data-tooltip-id="tooltip-id"
                                    data-tooltip-content="View the original post"
                                    href={socialMediaPostLink || ''}
                                    aria-label="Post"
                                    target="_blank"
                                    rel="noreferrer"
                                    onClick={() => {
                                        mixpanel.track('Content - post link clicked', {
                                            link: socialMediaPostLink
                                        })
                                    }}
                                >
                                    <span className="flex">
                                        <span className="hidden @lg:inline">Post</span>
                                        <ArrowTopRightOnSquareIcon className="w-4 @lg:ml-1" />
                                    </span>
                                </a>
                            ) : (
                                <span
                                    className="flex cursor-not-allowed text-ink-400"
                                    data-tooltip-id="tooltip-id"
                                    data-tooltip-html={postLinkUnavailable}
                                    aria-disabled="true"
                                    aria-label="Post"
                                >
                                    <span className="hidden @lg:inline">Post</span>
                                    <ArrowTopRightOnSquareIcon className="w-4 @lg:ml-1" />
                                </span>
                            )}
                        </div>
                        <div className="text-arwen-700 hover:text-arwen-800 px-2 ">
                            {socialMediaContentLink ? (
                                <a
                                    data-tooltip-id="tooltip-id"
                                    data-tooltip-content={showInTooltipText}
                                    href={socialMediaContentLink}
                                    aria-label="Comment"
                                    target="_blank"
                                    rel="noreferrer"
                                    onClick={() => {
                                        mixpanel.track('Content - comment link clicked', {
                                            link: socialMediaContentLink
                                        })
                                    }}
                                >
                                    <span className="flex">
                                        <span className="hidden @lg:inline">Comment</span>
                                        <ArrowTopRightOnSquareIcon className="w-4 @lg:ml-1" />
                                    </span>
                                </a>
                            ) : (
                                <span
                                    className="flex cursor-not-allowed text-ink-400"
                                    data-tooltip-id="tooltip-id"
                                    data-tooltip-html={linkUnavailableTooltipHtml}
                                    aria-disabled="true"
                                    aria-label="Comment"
                                >
                                    <span className="hidden @lg:inline">Comment</span>
                                    <ArrowTopRightOnSquareIcon className="w-4 @lg:ml-1" />
                                </span>
                            )}
                        </div>
                    </div>
                )}
            </div>
        </div>
    )
}
